import React, { useEffect, useRef, useState } from 'react';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstanceSection } from '../../hooks/useAuthorizedRequestForSection';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ErrorMessage } from '../../validation/validationError';
import { ZodError } from 'zod';
import { sectionStudentActivityFormValidation } from '../../validation/Validation';
import { Loader } from '../../components/ModalsCommonFunctions';

const SectionStudentActivity = () => {
    const authorizedAxiosSection = generateAxiosInstanceSection()
    let instituteMasterId = localStorage.getItem("instituteMasterId")
    let sectionMasterId = localStorage.getItem("sectionMasterId")

    const initialFormState = {
        day_type: '',
        start_time: '',
        end_time: '',
        activity: ''
    }
    const [activityData, setActivityData] = useState(initialFormState)
    const [activityList, setActivityList] = useState([])
    const [editId, setEditId] = useState(null)
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getActivityList();
    }, []);

    const getActivityList = () => {
        const apiUrl = `/get-student-activity?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}`;
        authorizedAxiosSection && authorizedAxiosSection.get(apiUrl)
            .then(response => {
                setActivityList(response.data.data)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.log("Error data fetching: ", errorMsg);
            });
    }

    const handleInputStartTimeChange = (time) => {
        const formattedTime = dayjs(time).format('h:mm A');
        setActivityData((prevData) => ({
            ...prevData,
            start_time: formattedTime
        }));

        if (formattedTime.trim() !== '') {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== "start_time"));
        }
    };

    const handleInputEndTimeChange = (time) => {
        const formattedTime = dayjs(time).format('h:mm A');
        setActivityData((prevData) => ({
            ...prevData,
            end_time: formattedTime
        }));

        if (formattedTime.trim() !== '') {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== "end_time"));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setActivityData((prevData) => {
            return {
                ...prevData,
                [name]: value
            }
        })

        if (value.trim().length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const addActivity = async () => {
        try {
            await sectionStudentActivityFormValidation.parseAsync(activityData);

            let newErrors = [];
            for (const [key, value] of Object.entries(activityData)) {
                if ((key === "start_time" || key === "end_time")
                    && dayjs(activityData.end_time, 'h:mm A').isBefore(dayjs(activityData.start_time, 'h:mm A'))) {

                    const errorIndex = newErrors.findIndex(err => err.path.includes("end_time"));
                    const errorMessage = "End time cannot be earlier than the start time";

                    if (errorIndex !== -1) {
                        // Update error message if it already exists
                        newErrors[errorIndex].message = errorMessage;
                    } else {
                        // Add new error message
                        newErrors.push({ path: ["end_time"], message: errorMessage });
                    }
                }
            }

            if (newErrors.length > 0) {
                setErrors(newErrors);
                return;
            } else {
                setErrors([]);
            }

            setLoading(true);
            const apiUrl = `/add-student-activity?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}`;
            authorizedAxiosSection && authorizedAxiosSection.post(apiUrl, activityData)
                .then(response => {
                    setActivityData(initialFormState);
                    getActivityList();
                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } catch (err) {
            if (err instanceof ZodError) {
                let newErrors = [...err.errors];
                for (const [key, value] of Object.entries(activityData)) {
                    if ((key === "start_time" || key === "end_time")
                        && dayjs(activityData.end_time, 'h:mm A').isBefore(dayjs(activityData.start_time, 'h:mm A'))) {

                        const errorIndex = newErrors.findIndex(err => err.path.includes("end_time"));
                        const errorMessage = "End time cannot be earlier than the start time";

                        if (errorIndex !== -1) {
                            // Update error message if it already exists
                            newErrors[errorIndex].message = errorMessage;
                        } else {
                            // Add new error message
                            newErrors.push({ path: ["end_time"], message: errorMessage });
                        }
                    }
                }
                setErrors(newErrors);
            }
        }
    }

    const updateStudentActivity = () => {
        setLoading(true);
        const apiUrl = `/update-student-activity?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}&id=${editId}`;
        authorizedAxiosSection && authorizedAxiosSection.put(apiUrl, activityData)
            .then(response => {
                setEditId(null);
                getActivityList();
                Swal.fire(
                    { text: response.data.message }
                );
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            })
            .finally(() => {
                setLoading(false); // End loading
            });
    }

    const deleteActivity = (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            setLoading(true);
            const apiUrl = `/delete-student-activity?id=${id}`;
            authorizedAxiosSection && authorizedAxiosSection.delete(apiUrl)
                .then(response => {
                    getActivityList();
                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        }
    }

    const handleInputReset = () => {
        setActivityData(initialFormState);
        setEditId(null);
        setErrors([])
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <Header />
                <div id='scroll-up'></div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb my-0 ms-2">
                        <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/section-dashboard')}>
                            <span>Dashboard</span>
                        </li>
                        <li className="breadcrumb-item active"><span>Student Activity</span></li>
                    </ol>
                </nav>

                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fas fa-book-reader"></i>Student Activity</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">

                                                <div className="col-md-12 view_content_radio_btn">
                                                    <label for="inputPassword" className="col-form-label"><span style={{ color: "red" }}>*</span>Days :</label>
                                                    <label>
                                                        <input type="radio" name="day_type" value="W" checked={activityData.day_type == "W"} onChange={handleInputChange} /> &nbsp;Working Day
                                                    </label>
                                                    <label>
                                                        <input type="radio" name="day_type" value="H" checked={activityData.day_type == "H"} onChange={handleInputChange} /> &nbsp;Hostel Day
                                                    </label>
                                                </div>
                                                <ErrorMessage errors={errors} field="day_type" />

                                                <div className="col-md-6">
                                                    <div className='cstm_time_picker'>
                                                        <label htmlFor="start_time" className="col-form-label"><span style={{ color: "red" }}>*</span>Start Time</label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DemoContainer components={['TimePicker', 'TimePicker']}>
                                                                <TimePicker
                                                                    value={dayjs(activityData.start_time, 'h:mm A')}
                                                                    onChange={(newTime) => handleInputStartTimeChange(newTime)}
                                                                />
                                                            </DemoContainer>
                                                        </LocalizationProvider>
                                                        <ErrorMessage errors={errors} field="start_time" />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className='cstm_time_picker'>
                                                        <label htmlFor="end_time" className="col-form-label"><span style={{ color: "red" }}>*</span>End Time</label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DemoContainer components={['TimePicker', 'TimePicker']}>
                                                                <TimePicker
                                                                    value={dayjs(activityData.end_time, 'h:mm A')}
                                                                    onChange={(newTime) => handleInputEndTimeChange(newTime)}
                                                                />
                                                            </DemoContainer>
                                                        </LocalizationProvider>
                                                        <ErrorMessage errors={errors} field="end_time" />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="activity" className="col-form-label"><span style={{ color: "red" }}>*</span>Activity:</label>
                                                    <textarea className="form-control" id="activity" name="activity" rows={3} onChange={handleInputChange} value={activityData.activity} />
                                                    <ErrorMessage errors={errors} field="activity" />
                                                </div>

                                            </div>

                                            <div className="row">
                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>

                                                    <div className="col-auto">
                                                        {
                                                            !editId && <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} onClick={addActivity}>Save</button>
                                                        }
                                                        <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleInputReset}>Reset</button>

                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateStudentActivity} disabled={!editId}>Update</button>)
                                                        }
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope='col'>Working Day/Hostel Day</th>
                                                                        <th scope="col">Start Time</th>
                                                                        <th scope="col">End Time</th>
                                                                        <th scope="col">Activity</th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        activityList.length > 0 ?
                                                                            activityList.map((activity, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row" className='sl_no'>{index + 1}</th>
                                                                                        <td>{activity.day_type === "W" ? "Working Day" : "Hostel Day"}</td>
                                                                                        <td>{activity.start_time}</td>
                                                                                        <td>{activity.end_time}</td>
                                                                                        <td>{activity.activity}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon " type="button" onClick={() => {
                                                                                                setActivityData({
                                                                                                    day_type: activity.day_type,
                                                                                                    start_time: activity.start_time,
                                                                                                    end_time: activity.end_time,
                                                                                                    activity: activity.activity
                                                                                                });
                                                                                                setEditId(activity.id);
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>

                                                                                            <button className="btn btn-outline-danger delete_icon" disabled={editId === activity.id} type="button" onClick={() => deleteActivity(activity.id)}>
                                                                                                <i class="fa-light fa-trash-can"></i>
                                                                                            </button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Activity Available</th>
                                                                            </tr>)
                                                                    }

                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default SectionStudentActivity;